import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Procedures.css';

function Procedures() {
  const [activeSection, setActiveSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSectionClick = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  const matchesSearch = (...keywords) => {
    const query = searchTerm.trim().toLocaleLowerCase();
    if (!query) return true;
    return keywords.some((keyword) => keyword.toLocaleLowerCase().includes(query));
  };

  const handleTableOfContentsClick = (event, sectionId, cardId = null) => {
    event.preventDefault();
    setSearchTerm('');
    if (cardId) setActiveSection(cardId);
    window.history.replaceState(null, '', `#${sectionId}`);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  return (
    <div className="procedures-container">
      <div className="procedures-header">
        <div className="header-left">
          <Link to="/" className="btn btn-secondary btn-back">
            <i className="fas fa-home"></i> Accueil
          </Link>
          <h1>Procédures d'Immigration au Canada - Guide Complet</h1>
        </div>
        <div className="search-procedures">
          <i className="fas fa-search"></i>
          <input 
            type="text" 
            placeholder="Rechercher dans le guide..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="table-of-contents">
        <h2>Table des Matières</h2>
        <ol>
          <li><a href="#par-ou-commencer" onClick={(event) => handleTableOfContentsClick(event, 'par-ou-commencer')}>Par où commencer</a></li>
          <li><a href="#immigration-temporaire" onClick={(event) => handleTableOfContentsClick(event, 'immigration-temporaire')}>Immigration Temporaire</a>
            <ul>
              <li><a href="#visa-visiteur" onClick={(event) => handleTableOfContentsClick(event, 'visa-visiteur', 'visa-visiteur')}>Visa de Visiteur</a></li>
              <li><a href="#permis-travail" onClick={(event) => handleTableOfContentsClick(event, 'permis-travail', 'permis-travail')}>Permis de Travail</a></li>
              <li><a href="#permis-etudes" onClick={(event) => handleTableOfContentsClick(event, 'permis-etudes', 'permis-etudes')}>Permis d'Études</a></li>
              <li><a href="#super-visa" onClick={(event) => handleTableOfContentsClick(event, 'super-visa', 'super-visa')}>Super visa (parents et grands-parents)</a></li>
            </ul>
          </li>
          <li><a href="#immigration-permanente" onClick={(event) => handleTableOfContentsClick(event, 'immigration-permanente')}>Immigration Permanente</a></li>
          <li><a href="#quebec" onClick={(event) => handleTableOfContentsClick(event, 'quebec', 'quebec')}>Cas particulier du Québec</a></li>
          <li><a href="#quebec-programmes" onClick={(event) => handleTableOfContentsClick(event, 'quebec-programmes', 'quebec')}>Programmes du Québec</a></li>
          <li><a href="#documents-etapes" onClick={(event) => handleTableOfContentsClick(event, 'documents-etapes', 'documents-etapes')}>Documents et étapes communes</a></li>
          <li><a href="#delais-traitement" onClick={(event) => handleTableOfContentsClick(event, 'delais-traitement')}>Délais de Traitement</a></li>
          <li><a href="#frais" onClick={(event) => handleTableOfContentsClick(event, 'frais')}>Frais</a></li>
          <li><a href="#frais-quebec" onClick={(event) => handleTableOfContentsClick(event, 'frais-quebec', 'frais-quebec')}>Frais du Québec</a></li>
          <li><a href="#ressources-officielles" onClick={(event) => handleTableOfContentsClick(event, 'ressources-officielles')}>Ressources officielles</a></li>
        </ol>
      </div>

      <div className="procedures-content">
        {/* Par où commencer */}
        <section id="par-ou-commencer" className="procedure-section">
          <h2><i className="fas fa-route"></i> Par où commencer ?</h2>
          <div className="procedure-card procedure-card--highlight">
            <div className="procedure-summary">
              <p><strong>Choisissez d'abord votre objectif :</strong> visiter, étudier, travailler, rejoindre votre famille ou vous établir de façon permanente. Les exigences, les formulaires et les frais dépendent du programme choisi.</p>
              <ol className="steps-list">
                <li><strong>Vérifier l'admissibilité</strong> sur le site d'IRCC et, le cas échéant, sur celui du Québec ou de la province concernée.</li>
                <li><strong>Réunir les documents</strong> : passeport, formulaires, preuves financières, études ou emploi, traductions et certificats requis.</li>
                <li><strong>Créer le bon compte</strong> (IRCC, portail de résidence permanente ou portail provincial) et remplir les formulaires à jour.</li>
                <li><strong>Payer les frais et fournir les biométries</strong> lorsque cela est demandé.</li>
                <li><strong>Suivre la demande</strong> et répondre avant la date limite à toute demande de document, d'examen médical ou d'entrevue.</li>
              </ol>
              <div className="important-note">
                <p><strong>À retenir :</strong> les délais, les quotas, les frais et les listes de documents changent. Il faut toujours confirmer la version actuelle sur IRCC avant de soumettre une demande.</p>
              </div>
            </div>
          </div>

        </section>

        {/* Immigration Temporaire */}
        <section id="immigration-temporaire" className="procedure-section">
          <h2><i className="fas fa-clock"></i> Immigration Temporaire</h2>
          
          {/* Visa de Visiteur */}
          <div id="visa-visiteur" className={`procedure-card ${matchesSearch('visa', 'visiteur', 'tourisme') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('visa-visiteur')}>
              <h3>1. Visa de Visiteur (Visa de Résident Temporaire - VRT)</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'visa-visiteur' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            <div className="procedure-summary">
              <p><strong>Description :</strong> Un visa de visiteur (aussi appelé visa de résident temporaire) est un document officiel apposé dans le passeport qui indique que vous satisfaites aux exigences nécessaires pour venir au Canada.</p>
              <div className="procedure-info">
                <span className="procedure-delay"><i className="fas fa-clock"></i> Délai : Varie selon le pays</span>
                <span className="procedure-cost"><i className="fas fa-dollar-sign"></i> Frais : 100 $ CAN par personne, sauf exemption</span>
              </div>
            </div>
            
            {activeSection === 'visa-visiteur' && (
              <div className="procedure-details">
                <div className="important-note">
                  <p><strong>Note importante :</strong> Il se peut que vous deviez fournir vos données biométriques avec votre demande. Ce délai de traitement ne tient pas compte du temps dont vous avez besoin pour fournir vos données biométriques.</p>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-list"></i> Sections à explorer :</h4>
                  <ul>
                    <li>Qui a besoin d'un visa de visiteur</li>
                    <li>Qui peut présenter une demande</li>
                    <li>Comment présenter une demande</li>
                    <li>Après avoir présenté une demande</li>
                  </ul>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-check-circle"></i> Critères d'admissibilité pour le visa de visiteur :</h4>
                  <ul>
                    <li>Posséder un titre de voyage valide (passeport)</li>
                    <li>Être en bonne santé</li>
                    <li>Ne pas avoir été reconnu coupable d'activités criminelles ou d'infractions liées à l'immigration</li>
                    <li>Convaincre un agent d'immigration que vous retournerez dans votre pays de résidence</li>
                    <li>Convaincre un agent d'immigration que vous quitterez le Canada à la fin de votre séjour</li>
                    <li>Avoir suffisamment d'argent pour votre séjour</li>
                    <li>Possibilité d'examen médical requis</li>
                    <li>Possibilité de lettre d'invitation nécessaire</li>
                  </ul>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-calendar"></i> Durée de séjour :</h4>
                  <ul>
                    <li>Majorité des personnes peuvent rester jusqu'à 6 mois au Canada</li>
                    <li>L'agent des services frontaliers peut autoriser une période différente</li>
                    <li>La durée de validité du visa est fixée par l'agent; elle ne garantit pas une durée de séjour déterminée</li>
                  </ul>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-plane"></i> Raisons de demande de visa de visiteur :</h4>
                  <ul>
                    <li>Tourisme</li>
                    <li>Affaires</li>
                    <li>Visite familiale</li>
                    <li>Visite d'époux/conjoint de fait pour résidence permanente</li>
                    <li>Raisons humanitaires</li>
                    <li>Rejoindre un navire en tant que membre d'équipage</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Permis de Travail */}
          <div id="permis-travail" className={`procedure-card ${matchesSearch('travail', 'employeur', 'EIMT') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('permis-travail')}>
              <h3>2. Permis de Travail Temporaire</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'permis-travail' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            <div className="procedure-summary">
              <p><strong>Description :</strong> La plupart des étrangers ont besoin d'un permis de travail pour travailler au Canada.</p>
              <div className="procedure-info">
                <span className="procedure-delay"><i className="fas fa-clock"></i> Délai : Vérifier les délais (varie selon le programme)</span>
                <span className="procedure-cost"><i className="fas fa-dollar-sign"></i> Frais : 155 $ CAN ; permis ouvert : 255 $ CAN, sauf exemption</span>
              </div>
            </div>
            
            {activeSection === 'permis-travail' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-briefcase"></i> Types de permis de travail :</h4>
                  
                  <h5>A) Permis de travail lié à un employeur donné :</h5>
                  <ul>
                    <li>Permet de travailler selon les conditions figurant sur le permis</li>
                    <li>Nom de l'employeur spécifique</li>
                    <li>Période de travail définie</li>
                    <li>Lieu de travail spécifique (le cas échéant)</li>
                  </ul>

                  <h5>Documents requis de l'employeur :</h5>
                  <ul>
                    <li>Copie du contrat de travail</li>
                    <li>Copie d'une étude d'impact sur le marché du travail (EIMT) OU</li>
                    <li>Numéro d'offre d'emploi (pour les travailleurs dispensés de l'EIMT)</li>
                  </ul>

                  <h5>B) Permis de travail ouvert :</h5>
                  <ul>
                    <li>Permet de travailler pour n'importe quel employeur au Canada</li>
                    <li>Exceptions : employeurs non admissibles ou offrant des services d'escorte/danse érotique</li>
                    <li>Disponible uniquement dans certaines situations spécifiques</li>
                  </ul>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-family"></i> Famille :</h4>
                  <p>L'époux/conjoint de fait et les enfants à charge peuvent parfois être admissibles à un statut temporaire; leurs critères dépendent de la situation du titulaire.</p>
                </div>
              </div>
            )}
          </div>

          {/* Permis d'Études */}
          <div id="permis-etudes" className={`procedure-card ${matchesSearch('études', 'etudes', 'étudiant', 'CAQ', 'PAL', 'TAL') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('permis-etudes')}>
              <h3>3. Permis d'Études</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'permis-etudes' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            <div className="procedure-summary">
              <p><strong>Description :</strong> Document délivré pour permettre aux étrangers d'étudier dans un établissement d'enseignement désigné (EED) au Canada.</p>
              <div className="procedure-info">
                <span className="procedure-delay"><i className="fas fa-clock"></i> Délai : consulter l'outil IRCC selon le pays</span>
                <span className="procedure-cost"><i className="fas fa-dollar-sign"></i> Frais : 150 $ CAN</span>
              </div>
            </div>
            
            {activeSection === 'permis-etudes' && (
              <div className="procedure-details">
                <div className="important-note">
                  <h4><i className="fas fa-exclamation-triangle"></i> Attestation pour le permis d'études :</h4>
                  <p><strong>Règle actuelle :</strong> la plupart des étudiants doivent joindre une lettre d'attestation provinciale ou territoriale (LAP/LAT). Au Québec, le CAQ sert d'attestation; il faut vérifier les exemptions et les conditions propres à l'année de la demande.</p>
                  <p><strong>Attention :</strong> une demande sans attestation valide lorsqu'elle est exigée peut être retournée sans traitement. L'attestation doit être valide au moment du dépôt.</p>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-user-check"></i> Critères d'admissibilité :</h4>
                  <ul>
                    <li>Être accepté dans un établissement d'enseignement désigné (EED)</li>
                    <li>Prouver que vous avez assez d'argent pour :
                      <ul>
                        <li>Payer les frais de scolarité</li>
                        <li>Subvenir aux besoins de subsistance (vous et votre famille)</li>
                        <li>Payer les frais de transport de retour</li>
                      </ul>
                    </li>
                    <li>Respecter la loi et n'avoir aucun casier judiciaire</li>
                    <li>Être en bonne santé (examen médical si requis)</li>
                    <li>Convaincre l'agent que vous quitterez le Canada à la fin de vos études</li>
                  </ul>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-file-alt"></i> Documents requis de base :</h4>
                  <ul>
                    <li>Passeport valide ou titre de voyage</li>
                    <li>Lettre d'acceptation de l'établissement d'enseignement désigné (EED)</li>
                    <li>Lettre d'attestation provinciale (LAP) ou territoriale (LAT)</li>
                    <li>Preuve de fonds suffisants</li>
                    <li>Photos conformes aux exigences</li>
                    <li>Formulaire de demande dûment rempli</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div id="super-visa" className={`procedure-card ${matchesSearch('super visa', 'parents', 'grands-parents') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('super-visa')}>
              <h3>4. Super visa pour parents et grands-parents</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'super-visa' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            <div className="procedure-summary">
              <p><strong>Description :</strong> cette option permet aux parents et grands-parents de visiter leur famille au Canada pour de longues périodes. Ce n'est pas une demande de résidence permanente.</p>
            </div>
            {activeSection === 'super-visa' && (
              <div className="procedure-details">
                <ul>
                  <li>Lettre d'invitation de l'enfant ou petit-enfant au Canada et preuve de revenus suffisants du répondant.</li>
                  <li>Assurance médicale privée conforme aux exigences fédérales et examen médical lorsque requis.</li>
                  <li>Le demandeur doit démontrer qu'il quittera le Canada à la fin de son séjour autorisé.</li>
                  <li>Les exigences et frais applicables sont ceux du super visa publiés par IRCC au moment du dépôt.</li>
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Immigration Permanente */}
        <section id="immigration-permanente" className="procedure-section">
          <h2><i className="fas fa-home"></i> Immigration Permanente</h2>
          
          {/* Entrée Express */}
          <div className={`procedure-card ${matchesSearch('entrée express', 'express', 'travailleurs qualifiés') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('entree-express')}>
              <h3>1. Entrée Express</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'entree-express' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            <div className="procedure-summary">
              <p><strong>Description :</strong> Système en ligne pour gérer les demandes d'immigration présentées par des travailleurs qualifiés.</p>
              <div className="procedure-info">
                <span className="procedure-delay"><i className="fas fa-clock"></i> Délai : Varie selon le programme</span>
                <span className="procedure-cost"><i className="fas fa-dollar-sign"></i> Demandeur principal : 1 590 $ CAN*</span>
              </div>
            </div>
            
            {activeSection === 'entree-express' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-list"></i> Trois programmes gérés par Entrée Express :</h4>
                  <ol>
                    <li>Catégorie de l'expérience canadienne</li>
                    <li>Programme des travailleurs qualifiés (fédéral)</li>
                    <li>Programme des travailleurs de métiers spécialisés (fédéral)</li>
                  </ol>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-route"></i> Procédure Entrée Express :</h4>
                  <ol>
                    <li>Créer un profil et entrer dans le bassin</li>
                    <li>Invitation des candidats ayant obtenu le plus grand nombre de points lors des rondes d'invitations</li>
                    <li>Si invité, remplir le formulaire de demande</li>
                    <li>Examen de la demande et prise de décision</li>
                  </ol>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-dollar-sign"></i> Frais détaillés :</h4>
                  <ul>
                    <li>Demandeur principal : 1 590 $ CAN* (990 $ de traitement + 600 $ de droit de résidence permanente)</li>
                    <li>Époux/conjoint : 1 590 $ CAN* (990 $ de traitement + 600 $ de droit de résidence permanente)</li>
                    <li>Enfant à charge : 270 $ CAN</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Programme des Candidats des Provinces */}
          <div className={`procedure-card ${matchesSearch('PCP', 'province', 'provincial', 'candidats') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('pcp')}>
              <h3>2. Programme des Candidats des Provinces (PCP)</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'pcp' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            <div className="procedure-summary">
              <p><strong>Description :</strong> Programme destiné aux travailleurs qui possèdent les compétences, la formation et l'expérience requises pour contribuer à l'essor économique d'une province ou d'un territoire en particulier.</p>
            </div>
            
            {activeSection === 'pcp' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-users"></i> Groupes ciblés par les volets provinciaux :</h4>
                  <ul>
                    <li>Étudiants</li>
                    <li>Gens d'affaires</li>
                    <li>Travailleurs spécialisés</li>
                    <li>Travailleurs semi-spécialisés</li>
                  </ul>
                </div>

                <div className="important-note">
                  <p><strong>Note importante :</strong> le Québec ne participe pas au Programme des candidats des provinces (PCP); il sélectionne lui-même certains immigrants. Le Nunavut n'a pas non plus de volet PCP. Vérifiez toujours le volet provincial visé.</p>
                </div>
              </div>
            )}
          </div>

          {/* Parrainage Familial */}
          <div className={`procedure-card ${matchesSearch('parrainage', 'famille', 'époux', 'parent') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('parrainage')}>
              <h3>3. Parrainage Familial</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'parrainage' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            <div className="procedure-summary">
              <p><strong>Description :</strong> Permet de parrainer les membres de votre famille afin qu'ils deviennent résidents permanents et puissent vivre, étudier et travailler au Canada.</p>
              <div className="procedure-info">
                <span className="procedure-delay"><i className="fas fa-clock"></i> Délai : consulter l'outil IRCC selon la catégorie</span>
              </div>
            </div>
            
            {activeSection === 'parrainage' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-heart"></i> Types de parrainage disponibles :</h4>
                  <ul>
                    <li><strong>Époux, conjoint de fait ou enfant à charge</strong> - délai variable selon la catégorie et le lieu de résidence</li>
                    <li><strong>Enfant adopté</strong> - Procédure spécifique</li>
                    <li><strong>Parents ou grands-parents</strong> - Programme avec invitation requise</li>
                    <li><strong>Autres membres de la famille</strong> - Circonstances spécifiques</li>
                  </ul>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-user-check"></i> Critères généraux pour le parrain :</h4>
                  <ul>
                    <li>Être citoyen canadien ou résident permanent</li>
                    <li>Résider au Canada (ou démontrer l'intention de retourner si citoyen à l'étranger)</li>
                    <li>Respecter les exigences financières</li>
                    <li>Signer un engagement de parrainage</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div className={`procedure-card ${matchesSearch('communautés', 'francophone', 'rural', 'réfugié', 'asile') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('autres-voies-rp')}>
              <h3>4. Autres voies de résidence permanente</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'autres-voies-rp' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            <div className="procedure-summary">
              <p>Selon le profil et la région visée, d'autres programmes fédéraux, communautaires ou humanitaires peuvent être pertinents.</p>
            </div>
            {activeSection === 'autres-voies-rp' && (
              <div className="procedure-details">
                <ul>
                  <li><strong>Voies communautaires rurales ou francophones :</strong> elles peuvent exiger une offre d'emploi et la recommandation d'une communauté participante; l'admissibilité dépend de chaque communauté.</li>
                  <li><strong>Programmes de pilotes fédéraux :</strong> ils peuvent ouvrir, fermer ou modifier leurs critères. Vérifiez toujours la page officielle du programme avant de préparer un dossier.</li>
                  <li><strong>Protection des réfugiés et demande d'asile :</strong> ces démarches sont réservées aux personnes qui craignent avec raison de subir de la persécution ou un danger dans leur pays. Elles suivent une procédure distincte et nécessitent des preuves détaillées.</li>
                  <li><strong>Citoyenneté :</strong> après l'obtention de la résidence permanente, une demande de citoyenneté peut devenir possible si les conditions de présence physique et les autres exigences sont respectées.</li>
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Cas particulier du Québec */}
        <section id="quebec" className="procedure-section">
          <h2><i className="fas fa-map-marker-alt"></i> Cas particulier du Québec</h2>
          <div id="quebec-programmes" className={`procedure-card ${matchesSearch('Québec', 'CAQ', 'CSQ', 'PSTQ', 'PEQ', 'province') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('quebec') }>
              <h3>Le Québec a ses propres étapes</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'quebec' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            <div className="procedure-summary">
              <p>Pour plusieurs programmes économiques destinés au Québec, il faut d'abord obtenir une sélection du Québec (par exemple un CSQ), puis présenter la demande fédérale de résidence permanente à IRCC. Les conditions et les formulaires ne sont pas les mêmes que pour les autres provinces.</p>
            </div>
            {activeSection === 'quebec' && (
              <div className="procedure-details">
                <ul>
                  <li><strong>Études :</strong> le CAQ sert d'attestation québécoise et doit être valide au moment de la demande de permis d'études.</li>
                  <li><strong>Travail :</strong> certaines situations exigent une démarche auprès du Québec en plus du permis fédéral.</li>
                  <li><strong>Résidence permanente :</strong> vérifiez les instructions du programme québécois avant de créer un profil Entrée express; le Québec ne sélectionne pas ses travailleurs qualifiés par le PCP.</li>
                  <li><strong>Famille :</strong> une demande de parrainage peut aussi être suivie d'un engagement auprès du Québec selon le lieu de résidence du répondant.</li>
                </ul>
                <div className="procedure-section">
                  <h4><i className="fas fa-map-signs"></i> Principales procédures québécoises</h4>
                  <ul>
                    <li><strong>PSTQ :</strong> déclaration d'intérêt dans Arrima, invitation du MIFI, demande de sélection permanente, obtention du CSQ, puis demande fédérale de résidence permanente.</li>
                    <li><strong>PEQ :</strong> voie destinée à certains diplômés du Québec et travailleurs étrangers temporaires ayant l'expérience et les conditions requises. La réception des demandes et les critères doivent être vérifiés avant tout dépôt.</li>
                    <li><strong>Études :</strong> admission dans un établissement, demande de CAQ pour études, puis permis d'études auprès d'IRCC.</li>
                    <li><strong>Travail temporaire :</strong> offre d'emploi, démarches de l'employeur (EIMT ou dispense selon le cas), CAQ lorsqu'il est requis, puis permis de travail fédéral.</li>
                    <li><strong>Regroupement familial :</strong> après l'admissibilité fédérale au parrainage, une demande d'engagement auprès du Québec peut être requise.</li>
                  </ul>
                </div>
                <div className="important-note">
                  <p><strong>Attention :</strong> l'invitation et la réception des demandes ne sont pas garanties. Utilisez Arrima et les pages du MIFI pour confirmer le programme disponible, le quota et les documents applicables à votre situation.</p>
                </div>
              </div>
            )}
          </div>

        </section>

        {/* Documents et étapes communes */}
        <section id="documents-etapes" className="procedure-section">
          <h2><i className="fas fa-folder-open"></i> Documents et étapes communes</h2>
          <div className={`procedure-card ${matchesSearch('documents', 'étapes', 'contrôle', 'retard', 'refus') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('documents-etapes')}>
              <h3>Liste de contrôle avant l'envoi</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'documents-etapes' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            <div className="procedure-summary">
              <p>Une demande complète réduit les retards. Utilisez toujours la liste de contrôle générée pour votre situation, car un document supplémentaire peut être exigé selon votre pays et votre programme.</p>
            </div>
            {activeSection === 'documents-etapes' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-file-alt"></i> Documents souvent demandés</h4>
                  <ul>
                    <li>Passeport et titres de voyage valides; anciennes pages de visas si demandées</li>
                    <li>Formulaires signés, photos conformes et preuve de paiement</li>
                    <li>Preuves d'études, d'emploi, d'expérience et de liens familiaux</li>
                    <li>Relevés bancaires et autres preuves de fonds selon le programme</li>
                    <li>Certificats de police et examen médical lorsqu'ils sont requis</li>
                    <li>Traduction certifiée de tout document qui n'est ni en français ni en anglais</li>
                  </ul>
                </div>
                <div className="procedure-section">
                  <h4><i className="fas fa-exclamation-circle"></i> Causes fréquentes de retard ou de refus</h4>
                  <ul>
                    <li>Formulaire incomplet, incohérence entre les réponses ou document manquant</li>
                    <li>Preuves financières insuffisantes ou fonds dont l'origine n'est pas expliquée</li>
                    <li>Objectif du séjour, projet d'études ou expérience professionnelle mal documenté</li>
                    <li>Antécédents médicaux, criminels ou d'immigration non déclarés</li>
                    <li>Traduction non conforme, document expiré ou mauvaise version du formulaire</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Délais de Traitement */}
        <section id="delais-traitement" className="procedure-section">
          <h2><i className="fas fa-hourglass-half"></i> Délais de Traitement Généraux</h2>
          
          <div className="important-note">
            <h4><i className="fas fa-exclamation-triangle"></i> Note importante :</h4>
            <p>Les délais de traitement varient selon plusieurs facteurs :</p>
            <ul>
              <li>Type de demande présentée</li>
              <li>Complétude de la demande</li>
              <li>Rapidité de traitement des demandes déjà reçues</li>
              <li>Facilité de vérification des renseignements fournis</li>
              <li>Temps de réponse aux demandes supplémentaires</li>
              <li>Autres facteurs externes</li>
            </ul>
          </div>

          <div className={`procedure-card ${matchesSearch('délai', 'temporaire', 'visiteur', 'travail', 'études') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('delais-temporaire')}>
              <h3>Immigration Temporaire - Délais</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'delais-temporaire' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            {activeSection === 'delais-temporaire' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-clock"></i> Délais par type de demande :</h4>
                  <ul>
                    <li><strong>Visa de visiteur :</strong> Varie selon le pays</li>
                    <li><strong>Permis de travail :</strong> consultez l'outil IRCC selon le pays et le type de demande</li>
                    <li><strong>Permis d'études :</strong> consultez l'outil IRCC selon le pays et le lieu de dépôt</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className={`procedure-card ${matchesSearch('délai', 'permanent', 'express', 'parrainage', 'province') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('delais-permanent')}>
              <h3>Immigration Permanente - Délais</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'delais-permanent' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            {activeSection === 'delais-permanent' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-calendar"></i> Délais par programme :</h4>
                  <ul>
                    <li><strong>Entrée Express :</strong> la norme de six mois concerne les demandes complètes; le délai réel peut varier</li>
                    <li><strong>Parrainage familial :</strong> consultez l'outil IRCC pour la catégorie et le lieu de résidence concernés</li>
                    <li><strong>Programme des candidats des provinces :</strong> Varie selon la province et le processus</li>
                  </ul>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-plus"></i> Délais supplémentaires à considérer :</h4>
                  <ul>
                    <li>Temps pour fournir les données biométriques (non inclus dans les délais)</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Frais */}
        <section id="frais" className="procedure-section">
          <h2><i className="fas fa-money-bill-wave"></i> Frais d'Immigration</h2>
          <div className="important-note">
            <p><strong>Vérification : 20 octobre 2026.</strong> Montants en dollars canadiens, vérifiés auprès d'IRCC et du MIFI. Il s'agit des frais de demande, pas des honoraires de votre bureau ni des montants facturés dans le CRM. Les exemptions et frais supplémentaires dépendent du dossier.</p>
          </div>
          
          <div className={`procedure-card ${matchesSearch('frais', 'temporaire', 'visiteur', 'travail', 'études') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('frais-temporaire')}>
              <h3>Immigration Temporaire - Frais</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'frais-temporaire' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            {activeSection === 'frais-temporaire' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-dollar-sign"></i> Frais par type de demande :</h4>
                  <ul>
                    <li><strong>Visa de visiteur, y compris super visa :</strong> 100 $ CAN par personne, sauf exemption. Assurance et examen médical du super visa non inclus.</li>
                    <li><strong>Visa de visiteur — famille admissible :</strong> maximum de 500 $ CAN pour 5 personnes ou plus présentant leur demande ensemble, au même moment et au même endroit.</li>
                    <li><strong>Prolongation du séjour de visiteur :</strong> 100 $ CAN par personne.</li>
                    <li><strong>Permis de travail :</strong> 155 $ CAN par personne, sauf exemption.</li>
                    <li><strong>Permis de travail ouvert :</strong> 255 $ CAN au total (155 $ de permis + 100 $ pour le titulaire d'un permis ouvert), sauf exemption.</li>
                    <li><strong>Permis d'Études :</strong> 150 $ CAN</li>
                    <li><strong>Permis de séjour temporaire :</strong> 246,25 $ CAN par personne. Ce permis est distinct du visa de visiteur.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className={`procedure-card ${matchesSearch('frais', 'permanent', 'express', 'parrainage') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('frais-permanent')}>
              <h3>Immigration Permanente - Frais</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'frais-permanent' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            {activeSection === 'frais-permanent' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-users"></i> Entrée Express :</h4>
                  <ul>
                    <li><strong>Demandeur principal :</strong> 1 590 $ CAN* (990 $ de traitement + 600 $ de droit de résidence permanente)</li>
                    <li><strong>Époux/conjoint de fait :</strong> 1 590 $ CAN* (990 $ de traitement + 600 $ de droit de résidence permanente)</li>
                    <li><strong>Enfant à charge :</strong> 270 $ CAN</li>
                    <li><strong>Sans paiement initial du droit de résidence permanente :</strong> 990 $ CAN par adulte. Les 600 $ CAN restants sont à payer avant l'obtention de la résidence permanente, sauf exemption. Ce droit ne s'applique pas aux enfants à charge.</li>
                  </ul>
                </div>

                <div className="procedure-section">
                  <h4><i className="fas fa-heart"></i> Parrainage Familial :</h4>
                  <ul>
                    <li><strong>Frais de parrainage :</strong> Varie selon le type de parrainage</li>
                    <li><strong>Parrainage d'un époux ou partenaire :</strong> 1 260 $ CAN avec droit de résidence permanente, ou 660 $ CAN sans ce droit*</li>
                    <li><strong>Enfant à charge inclus dans le parrainage d'un époux ou partenaire :</strong> 180 $ CAN par enfant, sans droit de résidence permanente.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div id="frais-quebec" className={`procedure-card ${matchesSearch('frais', 'Québec', 'PSTQ', 'PEQ', 'CAQ', 'Arrima') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('frais-quebec')}>
              <h3>Frais du Québec (MIFI) — à partir du 1er janvier 2026</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'frais-quebec' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            {activeSection === 'frais-quebec' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-dollar-sign"></i> Sélection permanente — PSTQ et PEQ</h4>
                  <ul>
                    <li><strong>Requérant principal :</strong> 940 $ CA</li>
                    <li><strong>Époux, épouse ou conjoint(e) de fait :</strong> 201 $ CA</li>
                    <li><strong>Chaque enfant à charge :</strong> 201 $ CA</li>
                  </ul>
                </div>
                <div className="procedure-section">
                  <h4><i className="fas fa-graduation-cap"></i> Sélection temporaire</h4>
                  <ul>
                    <li><strong>Étudiant étranger (CAQ pour études) :</strong> 135 $ CA</li>
                    <li><strong>Travailleur étranger temporaire (CAQ pour travail) :</strong> 233 $ CA</li>
                    <li><strong>Évaluation comparative des études effectuées hors du Québec :</strong> 141 $ CA, si elle est demandée ou utile pour votre démarche.</li>
                  </ul>
                </div>
                <div className="important-note">
                  <p><strong>À prévoir :</strong> ces frais du Québec s'ajoutent, lorsqu'ils s'appliquent, aux frais fédéraux d'IRCC (permis, résidence permanente, biométrie, examen médical, etc.). Ils sont en dollars canadiens, généralement non remboursables et ajustés chaque 1er janvier.</p>
                </div>
                <div className="procedure-section">
                  <h4><i className="fas fa-heart"></i> Engagement de parrainage au Québec</h4>
                  <ul>
                    <li><strong>Première personne parrainée :</strong> 335 $ CA</li>
                    <li><strong>Chaque autre personne parrainée :</strong> 135 $ CA</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className={`procedure-card ${matchesSearch('frais', 'biométrie', 'biometrie', 'médical', 'diplôme') ? '' : 'search-hidden'}`}>
            <div className="procedure-header" onClick={() => handleSectionClick('frais-additionnels')}>
              <h3>Frais Additionnels Communs</h3>
              <span className="procedure-toggle">
                <i className={`fas fa-chevron-${activeSection === 'frais-additionnels' ? 'up' : 'down'}`}></i>
              </span>
            </div>
            
            {activeSection === 'frais-additionnels' && (
              <div className="procedure-details">
                <div className="procedure-section">
                  <h4><i className="fas fa-fingerprint"></i> Frais supplémentaires :</h4>
                  <ul>
                    <li><strong>Données biométriques, si exigées :</strong> 85 $ CAN par personne ; maximum de 170 $ CAN pour une famille admissible présentant ses demandes ensemble (époux/conjoint et enfants à charge).</li>
                    <li><strong>Examen médical :</strong> Varie selon le pays et le médecin désigné</li>
                    <li><strong>Certificats de police :</strong> Varie selon le pays</li>
                    <li><strong>Évaluation des diplômes d'études (EDE) :</strong> selon l'organisme désigné. Exemple : WES, 264 $ CAN pour l'EDE destinée à IRCC, hors livraison, frais supplémentaires et TVH de 13 % lorsqu'elle s'applique.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className="important-note">
            <p><strong>* À vérifier avant chaque paiement :</strong> les totaux de résidence permanente indiqués avec ce droit l'incluent déjà : ne pas ajouter une seconde fois les 600 $ CAN. La biométrie, l'examen médical, les traductions, les assurances et les honoraires du bureau ne sont pas inclus. Les frais fédéraux de résidence permanente ont été révisés le 30 avril 2026 ; les tarifs peuvent évoluer après la date de vérification.</p>
          </div>
        </section>

        {/* Ressources officielles */}
        <section id="ressources-officielles" className="procedure-section">
          <h2><i className="fas fa-external-link-alt"></i> Ressources officielles</h2>
          <div className="official-resources">
            <a href="https://www.canada.ca/fr/immigration-refugies-citoyennete/services/demande.html" target="_blank" rel="noreferrer">
              <i className="fas fa-info-circle"></i>
              <span><strong>Portail IRCC</strong><small>Programmes, formulaires et étapes officielles</small></span>
            </a>
            <a href="https://www.canada.ca/fr/immigration-refugies-citoyennete/services/demande/verifier-delais-traitement.html" target="_blank" rel="noreferrer">
              <i className="fas fa-clock"></i>
              <span><strong>Délais de traitement</strong><small>Outil officiel à consulter avant chaque dossier</small></span>
            </a>
            <a href="https://ircc.canada.ca/francais/information/frais/bareme.asp" target="_blank" rel="noreferrer">
              <i className="fas fa-dollar-sign"></i>
              <span><strong>Frais IRCC</strong><small>Montants actuels et changements de frais</small></span>
            </a>
            <a href="https://www.quebec.ca/immigration" target="_blank" rel="noreferrer">
              <i className="fas fa-map-marker-alt"></i>
              <span><strong>Immigration Québec</strong><small>CAQ, CSQ et programmes québécois</small></span>
            </a>
            <a href="https://cdn-contenu.quebec.ca/cdn-contenu/immigration/publications/fr/TAB_frais_2026.pdf" target="_blank" rel="noreferrer">
              <i className="fas fa-dollar-sign"></i>
              <span><strong>Tarifs MIFI 2026</strong><small>Barème officiel des frais du Québec</small></span>
            </a>
            <a href="https://www.wes.org/fr/evaluations-et-frais/" target="_blank" rel="noreferrer">
              <i className="fas fa-graduation-cap"></i>
              <span><strong>Frais WES</strong><small>Exemple de coût d'une EDE et frais supplémentaires</small></span>
            </a>
            <a href="https://www.quebec.ca/immigration/permanente/travailleurs-qualifies/programme-selection-travailleurs-qualifies/presenter-demande" target="_blank" rel="noreferrer">
              <i className="fas fa-list-check"></i>
              <span><strong>PSTQ — Québec</strong><small>Étapes, invitations et frais de sélection permanente</small></span>
            </a>
            <a href="https://www.quebec.ca/immigration/permanente/travailleurs-qualifies/programme-experience-quebecoise/presenter-demande" target="_blank" rel="noreferrer">
              <i className="fas fa-briefcase"></i>
              <span><strong>PEQ — Québec</strong><small>Conditions, dépôt dans Arrima et frais applicables</small></span>
            </a>
          </div>
        </section>

        {/* Note de fin */}
        <div className="more-sections-note">
          <p><i className="fas fa-info-circle"></i> <strong>Important :</strong> ce guide est un outil d'orientation et ne remplace pas les instructions officielles ni un avis juridique. Les règles, les quotas, les frais et les délais peuvent changer; vérifiez toujours les liens officiels avant de déposer une demande.</p>
        </div>
      </div>
    </div>
  );
}

export default Procedures;
